import {
  type ObjectResultsAttributeColumnId,
  type ObjectResultsSystemColumnId,
  type ObjectResultsTableColumnId,
  type ObjectResultsTableColumnState,
} from '@bizzdesign/data-access-search';
import { type ObjectType } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import {
  type AttributeCategoryDefinitionDto,
  type AttributeDefinition,
} from '@bizzdesign/shared/models';

/**
 * Direction in which a column can be moved within its pinned/unpinned group.
 */
export type ColumnMoveDirection = 'left' | 'right';

/**
 * Identifies one of the two ordered groups of selected columns.
 */
export type SelectedColumnsGroup = 'pinned' | 'unpinned';

/**
 * What every configurable column carries, whatever its origin.
 *
 * @property width - Width in pixels, before any resize the user applies.
 * @property canHide - Whether the user is allowed to hide the column.
 * @property defaultOrder - Position of the column in the catalogue.
 */
type ObjectResultsColumnBase = {
  readonly width: number;
  readonly canHide: boolean;
  readonly defaultOrder: number;
};

/**
 * Static definition of one of the table's built-in columns.
 *
 * @property kind - Discriminant marking the column as built-in.
 * @property id - Stable column identifier, also persisted in the store.
 * @property className - Class put on the column's `col`, `th` and `td`, so a
 * cell can be styled and found by column. Carries no width: `width` does that,
 * through the `col` elements.
 * @property headerKey - `featureSearch.searchResults` key for the column label.
 */
export type ObjectResultsSystemColumnDefinition = ObjectResultsColumnBase & {
  readonly kind: 'system';
  readonly id: ObjectResultsSystemColumnId;
  readonly className:
    | 'bd-col-type'
    | 'bd-col-title'
    | 'bd-col-description'
    | 'bd-col-source'
    | 'bd-col-used-in'
    | 'bd-col-last-modified';
  readonly headerKey:
    | 'columnType'
    | 'columnTitle'
    | 'columnDescription'
    | 'columnSource'
    | 'columnUsedIn'
    | 'columnLastModified';
};

/**
 * Static definition of a column showing one attribute of the searched type.
 *
 * @property kind - Discriminant marking the column as attribute-backed.
 * @property id - Stable column identifier, also persisted in the store.
 * @property className - Class shared by every attribute column, put on the
 * column's `col`, `th` and `td`. Carries no width, as above.
 * @property label - Attribute name as the metamodel spells it. Not translated.
 * Lifted out of `attributeDefinition` because three templates render it.
 * @property categoryId - Attribute category the attribute belongs to.
 * @property categoryName - Display name of that category.
 * @property attributeDefinition - The attribute as the metamodel defines it,
 * which a cell needs to read the human-readable side of a reference value.
 */
export type ObjectResultsAttributeColumnDefinition = ObjectResultsColumnBase & {
  readonly kind: 'attribute';
  readonly id: ObjectResultsAttributeColumnId;
  readonly className: 'bd-col-attribute';
  readonly label: string;
  readonly categoryId: string;
  readonly categoryName: string;
  readonly attributeDefinition: AttributeDefinition;
};

/**
 * Static definition of a configurable column in the object results table.
 */
export type ObjectResultsColumnDefinition =
  | ObjectResultsSystemColumnDefinition
  | ObjectResultsAttributeColumnDefinition;

/**
 * A column definition combined with the user's current pin state for it.
 */
export type ObjectResultsColumn = ObjectResultsColumnDefinition & {
  readonly pinned: boolean;
};

/**
 * Every column the table can offer for one search, and a lookup by id.
 *
 * @property columns - The columns, in catalogue order: the built-in ones first.
 * @property byId - The same columns, keyed by id, for rehydrating a stored
 * layout without scanning the list once per column.
 */
export type ObjectResultsColumnCatalogue = {
  readonly columns: readonly ObjectResultsColumnDefinition[];
  readonly byId: ReadonlyMap<
    ObjectResultsTableColumnId,
    ObjectResultsColumnDefinition
  >;
};

/**
 * The table's built-in columns, in default order.
 */
export const OBJECT_RESULTS_SYSTEM_COLUMNS: readonly ObjectResultsSystemColumnDefinition[] =
  [
    {
      kind: 'system',
      id: 'type',
      className: 'bd-col-type',
      headerKey: 'columnType',
      // Design specified 40, which leaves 24px of content box: too narrow for
      // the header label and the column menu button the other columns carry.
      width: 104,
      canHide: true,
      defaultOrder: 0,
    },
    {
      kind: 'system',
      id: 'title',
      className: 'bd-col-title',
      headerKey: 'columnTitle',
      width: 344,
      canHide: false,
      defaultOrder: 1,
    },
    {
      kind: 'system',
      id: 'description',
      className: 'bd-col-description',
      headerKey: 'columnDescription',
      width: 364,
      canHide: true,
      defaultOrder: 2,
    },
    {
      kind: 'system',
      id: 'used-in',
      className: 'bd-col-used-in',
      headerKey: 'columnUsedIn',
      width: 374,
      canHide: true,
      defaultOrder: 3,
    },
    {
      kind: 'system',
      id: 'last-modified',
      className: 'bd-col-last-modified',
      headerKey: 'columnLastModified',
      width: 184,
      canHide: true,
      defaultOrder: 4,
    },
    {
      kind: 'system',
      id: 'source',
      className: 'bd-col-source',
      headerKey: 'columnSource',
      width: 154,
      canHide: true,
      defaultOrder: 5,
    },
  ];

const ATTRIBUTE_COLUMN_WIDTH_PX = 184;

const ATTRIBUTE_COLUMN_ID_PATTERN = /^attribute:[^:]+:.+$/;

const OBJECT_RESULTS_SYSTEM_COLUMN_IDS = new Set<string>(
  OBJECT_RESULTS_SYSTEM_COLUMNS.map(({ id }) => id),
);

/**
 * Builds the id of the column showing one attribute.
 *
 * @param categoryId - Attribute category the attribute belongs to.
 * @param attributeId - The attribute within that category.
 * @returns The column id.
 */
export function createAttributeColumnId(
  categoryId: string,
  attributeId: string,
): ObjectResultsAttributeColumnId {
  return `attribute:${categoryId}:${attributeId}`;
}

/**
 * Builds every column the table can offer for a search.
 *
 * @param attributeCategoryDefinitions - Every attribute category of the
 * metamodel, of every object type.
 * @param resolvedObjectType - The one object type every result will have, or
 * `undefined` when the search spans several types or pins none down.
 * @returns The built-in columns, followed by one column per attribute of the
 * resolved type. Only the built-in columns when no type resolves.
 *
 * @remarks
 * Categories are ordered by name, and the attributes within a category by name
 * too, matching how the details drawer lists the same attributes. The API
 * guarantees no order of its own, so without this the list would shuffle
 * between loads.
 */
export function createObjectResultsColumnCatalogue(
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[],
  resolvedObjectType: ObjectType | undefined,
): ObjectResultsColumnCatalogue {
  const columns: ObjectResultsColumnDefinition[] = [
    ...OBJECT_RESULTS_SYSTEM_COLUMNS,
  ];
  if (resolvedObjectType !== undefined) {
    const categories = attributeCategoryDefinitions
      .filter(({ elementType }) => elementType === resolvedObjectType)
      .sort((first, second) => first.name.localeCompare(second.name));
    for (const category of categories) {
      const attributeDefinitions = [...category.attributeDefinitions].sort(
        (first, second) => first.name.localeCompare(second.name),
      );
      for (const attributeDefinition of attributeDefinitions) {
        columns.push({
          kind: 'attribute',
          id: createAttributeColumnId(category.id, attributeDefinition.id),
          className: 'bd-col-attribute',
          label: attributeDefinition.name,
          categoryId: category.id,
          categoryName: category.name,
          attributeDefinition,
          width: ATTRIBUTE_COLUMN_WIDTH_PX,
          canHide: true,
          defaultOrder: columns.length,
        });
      }
    }
  }
  return {
    columns,
    byId: new Map(columns.map((column) => [column.id, column] as const)),
  };
}

/**
 * The catalogue a table shows when no single object type resolves: the built-in
 * columns and nothing else.
 */
export const OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE: ObjectResultsColumnCatalogue =
  createObjectResultsColumnCatalogue([], undefined);

/** Key of the group holding the table's built-in columns. */
export const SYSTEM_COLUMN_GROUP_KEY = 'system';

/**
 * One collapsible group of the columns a search offers: the built-in ones, or
 * the attributes of a single attribute category.
 *
 * @property kind - Whether the group holds built-in columns or attributes.
 * @property key - Stable group identifier, distinct across both kinds.
 * @property categoryName - Display name of the attribute category, empty for
 * the built-in group, whose label is translated instead.
 * @property columns - The group's columns, in catalogue order.
 */
export type ObjectResultsColumnGroup = {
  readonly kind: 'system' | 'attribute';
  readonly key: string;
  readonly categoryName: string;
  readonly columns: readonly ObjectResultsColumnDefinition[];
};

/**
 * Groups the columns a search offers for display in the manage-columns dialog.
 *
 * @param catalogue - The columns on offer for the current search.
 * @returns The built-in group, then one group per attribute category, in
 * catalogue order. A category defining no attribute yields no group.
 */
export function groupObjectResultsColumns(
  catalogue: ObjectResultsColumnCatalogue,
): readonly ObjectResultsColumnGroup[] {
  const columnsByKey = new Map<string, ObjectResultsColumnDefinition[]>();
  for (const column of catalogue.columns) {
    const key =
      column.kind === 'system'
        ? SYSTEM_COLUMN_GROUP_KEY
        : `category:${column.categoryId}`;
    const groupColumns = columnsByKey.get(key);
    if (groupColumns) {
      groupColumns.push(column);
    } else {
      columnsByKey.set(key, [column]);
    }
  }
  // A Map keeps its insertion order, so the groups come out in catalogue order.
  return [...columnsByKey].map(([key, columns]) => {
    const [firstColumn] = columns;
    return {
      kind: firstColumn.kind,
      key,
      categoryName:
        firstColumn.kind === 'system' ? '' : firstColumn.categoryName,
      columns,
    };
  });
}

/**
 * Narrows an arbitrary string to a well-formed object results column id.
 *
 * @param columnId - Candidate column identifier.
 * @returns `true` when the string is a built-in column id or has the shape of
 * an attribute column id.
 *
 * @remarks
 * Checks the shape only, so a caller holding no catalogue can read back an id
 * it wrote to the DOM itself. Use the catalogue's `byId` to tell whether a
 * column is actually on offer for the current search.
 */
export function isObjectResultsColumnId(
  columnId: string,
): columnId is ObjectResultsTableColumnId {
  return (
    OBJECT_RESULTS_SYSTEM_COLUMN_IDS.has(columnId) ||
    ATTRIBUTE_COLUMN_ID_PATTERN.test(columnId)
  );
}

/**
 * Builds the default column layout, with every built-in column shown and
 * unpinned.
 *
 * @returns The default ordered column list.
 *
 * @remarks
 * Never holds an attribute column: attribute columns start hidden and the user
 * adds them from the manage-columns dialog.
 */
export function createDefaultColumns(): ObjectResultsColumn[] {
  return OBJECT_RESULTS_SYSTEM_COLUMNS.map((column) => ({
    ...column,
    pinned: false,
  }));
}

const isStoredColumnStateValid = (
  storedColumns: ObjectResultsTableColumnState[],
  catalogue: ObjectResultsColumnCatalogue,
): boolean =>
  storedColumns.length > 0 &&
  storedColumns.every(({ id }) => catalogue.byId.has(id)) &&
  new Set(storedColumns.map(({ id }) => id)).size === storedColumns.length;

/**
 * Rehydrates the column layout from its persisted form, falling back to the
 * default layout when the stored state is empty, has duplicates, or names a
 * column the current search does not offer.
 *
 * @param storedColumns - Persisted column ids and pin states.
 * @param catalogue - The columns on offer for the current search.
 * @returns The ordered column list to render.
 */
export function createColumnsFromStoredState(
  storedColumns: ObjectResultsTableColumnState[],
  catalogue: ObjectResultsColumnCatalogue,
): ObjectResultsColumn[] {
  if (!isStoredColumnStateValid(storedColumns, catalogue)) {
    return createDefaultColumns();
  }
  return storedColumns.flatMap(({ id, pinned }) => {
    const column = catalogue.byId.get(id);
    return column ? [{ ...column, pinned }] : [];
  });
}

/**
 * Reduces a column layout to the shape persisted in the store.
 *
 * @param columns - Ordered column list to persist.
 * @returns The column ids and pin states, in the same order.
 */
export function toStoredColumnState(
  columns: ObjectResultsColumn[],
): ObjectResultsTableColumnState[] {
  return columns.map(({ id, pinned }) => ({ id, pinned }));
}

const getGroupEndIndex = (
  columns: ObjectResultsColumn[],
  targetGroup: ObjectResultsColumn[],
  pinned: boolean,
): number =>
  pinned
    ? targetGroup.length
    : columns.filter((column) => column.pinned).length + targetGroup.length;

/**
 * Inserts a column into the group matching its pin state, at the position its
 * `defaultOrder` gives it among the columns already there.
 *
 * @remarks
 * Only the inserted column moves: every other column keeps the position the
 * user gave it through move-left/right or drag-and-drop. Shared by pinning,
 * unpinning, and re-showing a hidden column so all three place a column the
 * same way.
 *
 * @param columns - Ordered column list to insert into, without the column.
 * @param column - Column to insert, carrying its target pin state.
 * @returns A new ordered column list including the inserted column.
 */
export function insertColumnByDefaultOrder(
  columns: ObjectResultsColumn[],
  column: ObjectResultsColumn,
): ObjectResultsColumn[] {
  const nextColumns = [...columns];
  const targetGroup = nextColumns.filter(
    (nextColumn) => nextColumn.pinned === column.pinned,
  );
  const nextColumnInTargetGroup = targetGroup.find(
    (nextColumn) => nextColumn.defaultOrder > column.defaultOrder,
  );
  const insertIndex = nextColumnInTargetGroup
    ? nextColumns.findIndex(({ id }) => id === nextColumnInTargetGroup.id)
    : getGroupEndIndex(nextColumns, targetGroup, column.pinned);
  nextColumns.splice(insertIndex, 0, column);
  return nextColumns;
}

/**
 * Flips the pin state of a column and re-places it in its new group.
 *
 * @param columns - Ordered column list to update.
 * @param columnId - Identifier of the column to pin or unpin.
 * @returns A new ordered column list, or the original when the column is absent.
 */
export function toggleColumnPin(
  columns: ObjectResultsColumn[],
  columnId: ObjectResultsTableColumnId,
): ObjectResultsColumn[] {
  const currentIndex = columns.findIndex(({ id }) => id === columnId);
  if (currentIndex === -1) {
    return columns;
  }
  const nextColumns = [...columns];
  const [column] = nextColumns.splice(currentIndex, 1);
  return insertColumnByDefaultOrder(nextColumns, {
    ...column,
    pinned: !column.pinned,
  });
}

/**
 * Shows a hidden column again, at the position its `defaultOrder` gives it
 * among the unpinned columns.
 *
 * @param columns - Ordered column list to update.
 * @param columnId - Identifier of the column to show.
 * @param catalogue - The columns on offer for the current search.
 * @returns A new ordered column list, or the original when the column is
 * already shown or the catalogue does not offer it.
 */
export function showColumn(
  columns: ObjectResultsColumn[],
  columnId: ObjectResultsTableColumnId,
  catalogue: ObjectResultsColumnCatalogue,
): ObjectResultsColumn[] {
  if (columns.some(({ id }) => id === columnId)) {
    return columns;
  }
  const definition = catalogue.byId.get(columnId);
  if (!definition) {
    return columns;
  }
  return insertColumnByDefaultOrder(columns, {
    ...definition,
    pinned: false,
  });
}

/**
 * Shows several hidden columns, each at the position its `defaultOrder` gives
 * it.
 *
 * @param columns - Ordered column list to update.
 * @param columnIds - Identifiers of the columns to show.
 * @param catalogue - The columns on offer for the current search.
 * @returns A new ordered column list.
 */
export function showColumns(
  columns: ObjectResultsColumn[],
  columnIds: readonly ObjectResultsTableColumnId[],
  catalogue: ObjectResultsColumnCatalogue,
): ObjectResultsColumn[] {
  return columnIds.reduce(
    (nextColumns, columnId) => showColumn(nextColumns, columnId, catalogue),
    columns,
  );
}

/**
 * Hides a column.
 *
 * @param columns - Ordered column list to update.
 * @param columnId - Identifier of the column to hide.
 * @returns A new ordered column list without that column.
 */
export function hideColumn(
  columns: ObjectResultsColumn[],
  columnId: ObjectResultsTableColumnId,
): ObjectResultsColumn[] {
  return columns.filter((column) => column.id !== columnId);
}

/**
 * Hides several columns, leaving alone any the user may not hide.
 *
 * @param columns - Ordered column list to update.
 * @param columnIds - Identifiers of the columns to hide.
 * @returns A new ordered column list.
 */
export function hideColumns(
  columns: ObjectResultsColumn[],
  columnIds: readonly ObjectResultsTableColumnId[],
): ObjectResultsColumn[] {
  const idsToHide = new Set<string>(columnIds);
  return columns.filter(
    (column) => !idsToHide.has(column.id) || !column.canHide,
  );
}

/**
 * Swaps a column with its neighbour inside its pinned/unpinned group.
 *
 * @param columns - Ordered column list to update.
 * @param columnId - Identifier of the column to move.
 * @param direction - Whether to move the column towards the start or the end.
 * @returns A new ordered column list, or the original when the move is a no-op.
 */
export function moveColumn(
  columns: ObjectResultsColumn[],
  columnId: ObjectResultsTableColumnId,
  direction: ColumnMoveDirection,
): ObjectResultsColumn[] {
  const currentIndex = columns.findIndex(({ id }) => id === columnId);
  if (currentIndex === -1) {
    return columns;
  }
  const currentColumn = columns[currentIndex];
  const columnIdsInGroup = columns
    .filter(({ pinned }) => pinned === currentColumn.pinned)
    .map(({ id }) => id);
  const currentGroupIndex = columnIdsInGroup.indexOf(columnId);
  const targetGroupIndex =
    direction === 'left' ? currentGroupIndex - 1 : currentGroupIndex + 1;
  if (targetGroupIndex < 0 || targetGroupIndex >= columnIdsInGroup.length) {
    return columns;
  }
  const targetColumnId = columnIdsInGroup[targetGroupIndex];
  const targetIndex = columns.findIndex(({ id }) => id === targetColumnId);
  const nextColumns = [...columns];
  [nextColumns[currentIndex], nextColumns[targetIndex]] = [
    nextColumns[targetIndex],
    nextColumns[currentIndex],
  ];
  return nextColumns;
}

/**
 * Reorders one pinned/unpinned group after a drag-and-drop, mapping the drop
 * indexes of the currently visible rows back onto the full group.
 *
 * @remarks
 * The dialog can be filtered while dragging, so the drop indexes refer to the
 * filtered rows only; columns hidden by the filter keep their slot in the group.
 *
 * @param groupColumns - Every column of the group, in current order.
 * @param visibleGroupColumns - The group's rows the user can currently see.
 * @param columnId - Identifier of the dragged column.
 * @param targetVisibleIndex - Index the column was dropped on, among the
 * visible rows.
 * @returns A new ordered group, or the original when the drop is not applicable.
 */
export function reorderColumnGroup(
  groupColumns: readonly ObjectResultsColumn[],
  visibleGroupColumns: readonly ObjectResultsColumn[],
  columnId: ObjectResultsTableColumnId,
  targetVisibleIndex: number,
): readonly ObjectResultsColumn[] {
  const previousIndex = visibleGroupColumns.findIndex(
    ({ id }) => id === columnId,
  );
  if (
    previousIndex === -1 ||
    targetVisibleIndex < 0 ||
    targetVisibleIndex >= visibleGroupColumns.length
  ) {
    return groupColumns;
  }

  const columnsWithoutDraggedColumn = visibleGroupColumns.filter(
    (_, index) => index !== previousIndex,
  );
  const reorderedVisibleColumns = [
    ...columnsWithoutDraggedColumn.slice(0, targetVisibleIndex),
    visibleGroupColumns[previousIndex],
    ...columnsWithoutDraggedColumn.slice(targetVisibleIndex),
  ];

  const visibleColumnIds = new Set(visibleGroupColumns.map(({ id }) => id));
  let reorderedVisibleColumnIndex = 0;
  return groupColumns.map((column) =>
    visibleColumnIds.has(column.id)
      ? reorderedVisibleColumns[reorderedVisibleColumnIndex++]
      : column,
  );
}
